/**
 * Token expiration states for session management
 */
export type TokenExpirationState = 'valid' | 'grace-period' | 'expired';

/**
 * Decoded minimal JWT payload (only the fields we need)
 */
interface JwtPayload {
    sub: string;
    email: string;
    exp: number;
    firstName?: string;
    lastName?: string;
    accountId?: string;
}

/**
 * Grace period duration in minutes.
 * Must match backend Jwt:RefreshGracePeriodMinutes setting.
 */
const GRACE_PERIOD_MINUTES = 5;

/**
 * Decode a JWT token payload without verification.
 * Only used client-side for reading claims — the backend validates signatures.
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        const payload = parts[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const jsonString = atob(base64);
        return JSON.parse(jsonString) as JwtPayload;
    } catch {
        return null;
    }
}

/**
 * Determine the expiration state of a JWT token.
 * - 'valid': token has not expired yet
 * - 'grace-period': token expired within GRACE_PERIOD_MINUTES
 * - 'expired': token expired more than GRACE_PERIOD_MINUTES ago
 */
export function getTokenExpirationState(token: string): TokenExpirationState {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return 'expired';

    const now = Math.floor(Date.now() / 1000);
    const exp = payload.exp;

    if (now < exp) {
        return 'valid';
    }

    const minutesSinceExpiry = (now - exp) / 60;
    if (minutesSinceExpiry <= GRACE_PERIOD_MINUTES) {
        return 'grace-period';
    }

    return 'expired';
}

/**
 * Check if a token is expired (regardless of grace period)
 */
export function isTokenExpired(token: string): boolean {
    return getTokenExpirationState(token) !== 'valid';
}
